/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { IHeaderViewProps } from '@msdyn365-commerce-modules/header/dist/types/modules/header/header';
import { Button, Module, Node, Popover } from '@msdyn365-commerce-modules/utilities';
import { getUrlSync } from '@msdyn365-commerce/core';
import classnames from 'classnames';
import React, {useState} from 'react';
import { IHeaderProps, IStoreSwitcherData } from '../definition-extensions/header.ext.props.autogenerated';

// tslint:disable-next-line: max-func-body-length
const headerView: React.FC<IHeaderViewProps & IHeaderProps<{}>> = props => {
    const { config, HeaderTag, HeaderContainer, HeaderTopBarContainer } = props;
    const showCartIcon = config.cartIcon;
    const showAccountBlock = config.accountBlock;

    const { activeStore, activeStoreClass, activeStoreIndex, storeSwitcher } = config;
    const [storePopoverOpen, _togglePopover] = useState(false);
    const storePopoverRef: React.RefObject<HTMLButtonElement> = React.createRef();
    const activeLink = {linkText: activeStore, linkUrl: {destinationUrl: ''}, ariaLabel: 'active'};
    const activeLinkIndex = activeStoreIndex || 0;
    const storeSwitcherDesktop = storeSwitcher && storeSwitcher.length && _insertStoreSwitcher(storeSwitcher, activeLink, activeLinkIndex);
    const storeSwitcherMobileId = 'storeSwitcherDropdown';
    const cartLink = getUrlSync('cart', props.context.actionContext);

    return (
        <Module {...HeaderTag}>
            {storeSwitcherDesktop && storeSwitcherDesktop.length &&
                <div className='ms-header__storeswitcher__desktop'>
                    {_renderStoreSwitcher(storeSwitcherDesktop, 'ms-header__storeswitcher')}
                </div>
            }
            <Node {...HeaderContainer}>
                {props.logo}
                <div className='ms-header__bars'>
                    <div className='ms-header__cart-bar__desktop'>
                        <a className='ms-header__storebox' href={cartLink}>
                            {activeStore &&
                                <div className={classnames('ms-header__storebox-text', activeStoreClass)}>
                                    {activeStore}
                                </div>
                            }
                            {showCartIcon && props.cartIcon}
                        </a>
                    </div>
                    <Node {...HeaderTopBarContainer}>
                        <div className='ms-header__nav__desktop'>
                            {props.menuBar && _renderReactFragment(props.menuBar)}
                            {props.slots && props.slots.smweMenuBar && _renderReactFragment(props.slots.smweMenuBar)}
                        </div>
                        {_renderReactFragment(props.search)}
                        {showAccountBlock && _renderAccountBlock(props)}
                        <div className='ms-header__nav__mobile'>
                            {props.menuBar && _renderReactFragment(props.menuBar)}
                            {props.slots && props.slots.smweMenuBar && _renderReactFragment(props.slots.smweMenuBar)}
                        </div>
                    </Node>
                </div>
            </Node>
            <div className='ms-header__cart-bar__mobile'>
                {storeSwitcher && storeSwitcher.length &&
                    <div className='ms-header__storeswitcher__mobile'>
                        <Button
                            innerRef={storePopoverRef}
                            className='ms-header__storeswitcher__mobile-button'
                            aria-describedby={storeSwitcherMobileId}
                            // tslint:disable-next-line:jsx-no-lambda react-this-binding-issue
                            onClick={() => _togglePopover(!storePopoverOpen)}
                            aria-expanded={storePopoverOpen}
                        >
                            {activeStore}
                        </Button>
                        <Popover
                            id={storeSwitcherMobileId}
                            className='ms-header__storeswitcher__mobile-popover'
                            placement='bottom'
                            isOpen={storePopoverOpen}
                            target={storePopoverRef}
                            // tslint:disable-next-line:jsx-no-lambda react-this-binding-issue
                            toggle={() => _togglePopover(!storePopoverOpen)}
                        >
                            {_renderStoreSwitcher(storeSwitcher, 'ms-header__storeswitcher')}
                        </Popover>
                    </div>
                }
                {showCartIcon && props.cartIcon}
            </div>
        </Module>
    );
};

function _renderAccountBlock(props: IHeaderViewProps): JSX.Element | null {
    const {
        AccountInfoDropdownParentContainer,
        AccountInfoDropdownPopoverConentContainer,
        accountInfoDropdownButton,
        signOutLink,
        signInLink,
        accountLinks
    } = props;

    if (AccountInfoDropdownParentContainer) {
        if (AccountInfoDropdownPopoverConentContainer) {
            return (
                <Node {...AccountInfoDropdownParentContainer}>
                    {accountInfoDropdownButton}
                    <Node {...AccountInfoDropdownPopoverConentContainer}>
                        {accountLinks ? accountLinks.map(link => link) : false}
                        {signOutLink}
                    </Node>
                </Node>
            );
        } else if (signInLink) {
            return <Node {...AccountInfoDropdownParentContainer}>{signInLink}</Node>;
        }
    }

    return null;
}

function _renderReactFragment(items: React.ReactNode[]): JSX.Element | null {
    return (
        <React.Fragment>
            {items && items.length
                ? items.map((slot: React.ReactNode, index: number) => {
                    return <React.Fragment key={index}>{slot}</React.Fragment>;
                })
            : null}
        </React.Fragment>
    );
}

function _renderStoreSwitcher(links: IStoreSwitcherData[], className: string): JSX.Element | null {
    return (
        <div className={className}>
            <ul className={`${className}-list`}>
                {links && links.length ? links.map((link: IStoreSwitcherData, index: number) => {
                    return (
                        <li key={index} className={`${className}-item`}>
                            <a
                                key={index}
                                className={`${className}-link`}
                                role='button'
                                title={link.linkText}
                                href={link.linkUrl && link.linkUrl.destinationUrl}
                                aria-label={link.ariaLabel}
                                target={link.openInNewTab ? 'blank' : undefined}
                            >
                                {link.linkText}
                            </a>
                        </li>
                    );
                }) : null}
            </ul>
        </div>
    );
}

function _insertStoreSwitcher(
    links: IStoreSwitcherData[],
    activeLink: IStoreSwitcherData,
    activeLinkIndex: number
): IStoreSwitcherData[] {
    // Grabbing links from the start of array to active link index
    const startLinks = links.slice(0, activeLinkIndex);

    // Grabbing links from active link index to end of array
    const endLinks = links.slice(activeLinkIndex);

    // Making a new array with start links, new active link to be inserted, and end links
    return [...startLinks, activeLink, ...endLinks];
}

export default headerView;
